import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useCompare } from "@/context/CompareContext";
import { useCart } from "@/context/CartContext";
import type { Product } from "@/types/product";

function StarRating({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-1 justify-center">
      <div className="flex items-center gap-px">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`text-xs ${
              star <= Math.floor(rating)
                ? "ri-star-fill text-amber-400"
                : star - rating < 1
                ? "ri-star-half-fill text-amber-400"
                : "ri-star-line text-gray-300"
            }`}
          ></i>
        ))}
      </div>
      <span className="text-xs text-gray-400">({reviews.toLocaleString()})</span>
    </div>
  );
}

const SPEC_ROWS: { label: string; key: keyof Product | "savings" | "availability" }[] = [
  { label: "Category", key: "category" },
  { label: "Brand", key: "brand" },
  { label: "Capacity", key: "capacity" },
  { label: "Wattage / Output", key: "watts" },
  { label: "Price", key: "price" },
  { label: "Savings", key: "savings" },
  { label: "Availability", key: "availability" },
];

function getCategoryLabel(cat: string) {
  if (cat === "solar") return "Solar Generator";
  if (cat === "gas") return "Gas Generator";
  if (cat === "whole-home") return "Whole-Home System";
  return cat;
}

function renderSpecValue(product: Product, key: string) {
  if (key === "category") return <span className="capitalize">{getCategoryLabel(product.category)}</span>;
  if (key === "brand") return <span className="font-semibold text-gray-900">{product.brand}</span>;
  if (key === "capacity") return product.capacity || <span className="text-gray-300">—</span>;
  if (key === "watts") return product.watts || <span className="text-gray-300">—</span>;
  if (key === "price") {
    return (
      <div className="flex flex-col items-center">
        <span className={`font-extrabold text-lg ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
          ${product.price.toLocaleString()}
        </span>
        {product.originalPrice && (
          <span className="text-xs text-gray-400 line-through">${product.originalPrice.toLocaleString()}</span>
        )}
      </div>
    );
  }
  if (key === "savings") {
    if (!product.originalPrice) return <span className="text-gray-300">—</span>;
    const save = product.originalPrice - product.price;
    const pct = Math.round((save / product.originalPrice) * 100);
    return (
      <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-bold px-2 py-1 rounded">
        Save ${save.toLocaleString()} ({pct}% off)
      </span>
    );
  }
  if (key === "availability") {
    return product.inStock ? (
      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
        <i className="ri-checkbox-circle-fill"></i> In Stock
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-gray-400 text-xs font-semibold">
        <i className="ri-close-circle-fill"></i> Out of Stock
      </span>
    );
  }
  return <span className="text-gray-300">—</span>;
}

export default function CompareModal() {
  const { compareList, removeFromCompare, clearCompare, modalOpen, closeModal } = useCompare();
  const { addItem } = useCart();
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") closeModal(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [modalOpen, closeModal]);

  if (!modalOpen) return null;

  const handleAddToCart = (product: Product) => {
    if (!product.inStock) return;
    addItem({ id: product.id, name: product.name, brand: product.brand, price: product.price, image: product.image });
  };

  const colWidth = compareList.length === 2 ? "w-1/2" : "w-1/3";

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-6 px-4"
      onClick={(e) => { if (e.target === overlayRef.current) closeModal(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-5xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 sticky top-0 z-10">
          <div>
            <h2 className="text-lg font-extrabold text-gray-900">Side-by-Side Comparison</h2>
            <p className="text-xs text-gray-500 mt-0.5">Comparing {compareList.length} products</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={clearCompare}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer px-2 py-1.5 whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1"></i> Clear All
            </button>
            <button
              onClick={closeModal}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Product header row */}
        <div className="flex border-b border-gray-100">
          {/* Row label col */}
          <div className="w-40 shrink-0 px-5 py-6 bg-gray-50 border-r border-gray-100">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Product</p>
          </div>

          {compareList.map((product) => (
            <div key={product.id} className={`${colWidth} px-4 py-5 border-r border-gray-100 last:border-r-0 relative`}>
              <button
                onClick={() => removeFromCompare(product.id)}
                className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 rounded transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
              <div className="w-full h-36 bg-gray-50 rounded-lg overflow-hidden mb-3 flex items-center justify-center">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain p-3" />
              </div>
              <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-0.5">{product.brand}</p>
              <Link
                to={`/shop/${product.id}`}
                onClick={closeModal}
                className="font-semibold text-gray-900 text-sm leading-snug hover:text-amber-600 transition-colors cursor-pointer line-clamp-2"
              >
                {product.name}
              </Link>
              <div className="mt-2">
                <StarRating rating={product.rating} reviews={product.reviews} />
              </div>
            </div>
          ))}
        </div>

        {/* Spec rows */}
        {SPEC_ROWS.map((row, i) => (
          <div
            key={row.key}
            className={`flex border-b border-gray-100 last:border-b-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
          >
            <div className="w-40 shrink-0 px-5 py-4 bg-gray-50 border-r border-gray-100 flex items-center">
              <p className="text-xs font-semibold text-gray-500 whitespace-nowrap">{row.label}</p>
            </div>
            {compareList.map((product) => (
              <div
                key={product.id}
                className={`${colWidth} px-4 py-4 border-r border-gray-100 last:border-r-0 flex items-center justify-center text-center text-sm text-gray-700`}
              >
                {renderSpecValue(product, row.key)}
              </div>
            ))}
          </div>
        ))}

        {/* Highlights rows */}
        <div className="flex border-b border-gray-100 bg-white">
          <div className="w-40 shrink-0 px-5 py-4 bg-gray-50 border-r border-gray-100 flex items-start pt-5">
            <p className="text-xs font-semibold text-gray-500">Highlights</p>
          </div>
          {compareList.map((product) => (
            <div key={product.id} className={`${colWidth} px-4 py-4 border-r border-gray-100 last:border-r-0`}>
              <ul className="space-y-1.5">
                {product.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                    <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-emerald-500 text-xs"></i>
                    </div>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust + shipping row */}
        <div className="flex border-b border-gray-100 bg-gray-50/50">
          <div className="w-40 shrink-0 px-5 py-4 bg-gray-50 border-r border-gray-100 flex items-center">
            <p className="text-xs font-semibold text-gray-500">Included</p>
          </div>
          {compareList.map((product) => (
            <div key={product.id} className={`${colWidth} px-4 py-4 border-r border-gray-100 last:border-r-0 flex flex-col items-center gap-1.5`}>
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <i className="ri-truck-line"></i> Free Shipping
              </span>
              <span className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                <i className="ri-shield-check-line"></i> Mfr. Warranty
              </span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                <i className="ri-arrow-go-back-line"></i> 30-Day Returns
              </span>
            </div>
          ))}
        </div>

        {/* Add to Cart row */}
        <div className="flex bg-white px-0 sticky bottom-0 border-t border-gray-200">
          <div className="w-40 shrink-0 bg-gray-50 border-r border-gray-100 flex items-center px-5">
            <p className="text-xs font-semibold text-gray-500 whitespace-nowrap">Add to Cart</p>
          </div>
          {compareList.map((product) => (
            <div key={product.id} className={`${colWidth} px-4 py-4 border-r border-gray-100 last:border-r-0 flex flex-col items-center gap-2`}>
              <button
                onClick={() => handleAddToCart(product)}
                disabled={!product.inStock}
                className={`w-full py-2.5 rounded-md text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                  product.inStock
                    ? "bg-amber-500 hover:bg-amber-400 text-white"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {product.inStock ? "Add to Cart" : "Out of Stock"}
              </button>
              <Link
                to={`/shop/${product.id}`}
                onClick={closeModal}
                className="text-xs text-gray-500 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                View Details →
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
